import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { cloneElement, memo, useCallback, useMemo, useRef, useState } from 'react'

import { cn } from '@repo/utils'

import { Button } from '../Button'
import { Modal } from '../Modal'

import type { FC, ReactElement } from 'react'

import type { AccentColor } from '../../constants'
import type { ModalProps } from '../Modal'

export interface ModalConfirmProps extends Omit<ModalProps, 'onOpenChange'> {
  onConfirm: () => void

  accent?: AccentColor
  confirmText?: string
  form?: string
  icon?: ReactElement
  onCancel?: () => void
}

const ModalConfirmComponent: FC<ModalConfirmProps> = ({
  accent = 'default',
  cancelText = 'Cancelar',
  className,
  confirmText = 'Confirmar',
  contentClassName,
  disableUserActions = false,
  footerActions,
  form,
  header,
  headerComment,
  icon,
  onCancel,
  onConfirm,
  ...props
}) => {
  const Icon = icon || <ExclamationTriangleIcon />

  return (
    <Modal
      {...props}
      cancelText={cancelText}
      className={cn('ModalConfirm', className)}
      contentClassName={cn('text-center', contentClassName)}
      disableUserActions={disableUserActions}
      footerActions={
        <>
          <Button
            accent={accent}
            className="w-full sm:w-auto"
            disabled={disableUserActions}
            form={form}
            onClick={onConfirm}
            type={form ? 'submit' : 'button'}
          >
            {confirmText}
          </Button>
          <Button className="w-full sm:w-auto" disabled={disableUserActions} onClick={onCancel}>
            {cancelText}
          </Button>
          {footerActions}
        </>
      }
      header={
        <div className="flex flex-col gap-2 items-center sm:flex-row">
          <div
            className={cn(
              'mx-auto flex size-10 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-8',
              accent === 'blue' && 'bg-blueLightest border border-blueLight',
              accent === 'default' && 'bg-white border border-greyLight',
              accent === 'gray' && 'bg-greyLightest border border-greyDark',
              accent === 'green' && 'bg-greenLightest border border-greenLight',
              accent === 'orange' && 'bg-orangeLightest border border-orangeLight',
              accent === 'red' && 'bg-redLightest border border-redLight',
              accent === 'teal' && 'bg-transparent border border-tealLight',
            )}
          >
            {cloneElement(Icon, {
              'aria-hidden': 'true',
              className: cn(
                'size-5',
                accent === 'blue' && 'text-blue',
                accent === 'default' && 'text-text',
                accent === 'gray' && 'text-grey',
                accent === 'green' && 'text-green',
                accent === 'orange' && 'text-orange',
                accent === 'red' && 'text-red',
                accent === 'teal' && 'text-teal',
              ),
            })}
          </div>
          <div>
            {header}
            {headerComment && <div className="text-xs font-normal">{headerComment}</div>}
          </div>
        </div>
      }
      onOpenChange={onCancel}
    />
  )
}

export const ModalConfirm = memo(ModalConfirmComponent)

export type ModalConfirmHookProps = Omit<ModalConfirmProps, 'initialOpen' | 'onCancel' | 'onConfirm' | 'open'>

export const useModalConfirm = ({ keepAfterConfirm = false }: { keepAfterConfirm?: boolean } = {}) => {
  const isOpenRef = useRef(false)
  const [, forceUpdate] = useState({})
  const resolveRef = useRef<(value: boolean) => void>()
  const validatorRef = useRef<() => boolean | Promise<boolean>>()

  const confirm = useCallback((validator?: () => boolean | Promise<boolean>) => {
    validatorRef.current = validator
    // Order matters in the next 2 lines
    isOpenRef.current = true
    forceUpdate({})

    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve
    })
  }, [])

  const handleClose = useCallback(() => {
    isOpenRef.current = false
    // Order matters in the next 2 lines
    resolveRef.current?.(false)
    forceUpdate({})
  }, [])

  const handleConfirm = useCallback(async () => {
    const validator = validatorRef.current
    if (validator && !(await validator())) {
      return
    }

    resolveRef.current?.(true)
    if (!keepAfterConfirm) {
      isOpenRef.current = false
      forceUpdate({})
    }
  }, [keepAfterConfirm])

  // Component created ONCE - only depends on stable callbacks
  const Confirm = useMemo<FC<ModalConfirmHookProps>>(
    () => (props) => (
      <ModalConfirm {...props} onCancel={handleClose} onConfirm={handleConfirm} open={isOpenRef.current} />
    ),
    [handleClose, handleConfirm],
  )

  return [Confirm, confirm, handleClose] as const
}
